/**
 * Utility functions for handling UTM parameters
 */

// List of UTM and tracking parameters that we should preserve
export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
  'wbraid',
  'gbraid'
];

/**
 * Extracts UTM parameters from the current URL
 * @returns {Object} Object with found UTM parameters
 */
export const getUtmParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const params = {};

  UTM_KEYS.forEach(key => {
    const value = searchParams.get(key);
    if (value) {
      params[key] = value;
    }
  });

  return params;
};

/**
 * Saves UTM parameters to sessionStorage
 * @param {Object} params - UTM parameters to save
 */
export const saveUtmParams = (params) => {
  if (Object.keys(params).length > 0) {
    sessionStorage.setItem('utm_params', JSON.stringify(params));
  }
};

/**
 * Retrieves UTM parameters from sessionStorage
 * @returns {Object} Object with saved UTM parameters
 */
export const getSavedUtmParams = () => {
  const saved = sessionStorage.getItem('utm_params');
  return saved ? JSON.parse(saved) : {};
};

/**
 * Combines UTM parameters from URL and sessionStorage
 * Prioritizes current URL parameters
 * @returns {Object} Object with all UTM parameters
 */
export const getCombinedUtmParams = () => {
  const urlParams = getUtmParams();
  const savedParams = getSavedUtmParams();

  // URL params have priority over saved ones
  return { ...savedParams, ...urlParams };
};

/**
 * Adds UTM parameters to a URL
 * @param {string} url - Base URL
 * @param {Object} params - UTM parameters to add (optional, uses saved ones if not provided)
 * @returns {string} URL with added UTM parameters
 */
export const addUtmParamsToUrl = (url, params = null) => {
  const utmParams = params || getCombinedUtmParams();

  if (Object.keys(utmParams).length === 0) {
    return url;
  }

  const urlObj = new URL(url, window.location.origin);

  Object.entries(utmParams).forEach(([key, value]) => {
    urlObj.searchParams.set(key, value);
  });

  return urlObj.toString();
};

/**
 * Generates query string with UTM parameters
 * @param {Object} params - UTM parameters (optional, uses saved ones if not provided)
 * @returns {string} Query string (e.g., "?utm_source=google&utm_medium=cpc")
 */
export const getUtmQueryString = (params = null) => {
  const utmParams = params || getCombinedUtmParams();

  if (Object.keys(utmParams).length === 0) {
    return '';
  }

  const searchParams = new URLSearchParams(utmParams);
  return `?${searchParams.toString()}`;
};

/**
 * Hook to initialize and save UTMs automatically
 * Should be called in the root component or entry pages
 */
export const initializeUtmTracking = () => {
  const urlParams = getUtmParams();

  if (Object.keys(urlParams).length > 0) {
    saveUtmParams(urlParams);
  }
};

/**
 * Clears saved UTM parameters
 */
export const clearUtmParams = () => {
  sessionStorage.removeItem('utm_params');
};
